import React, { useCallback, useEffect, useRef, useState } from "react";
import {
  Alert,
  AlertTitle,
  Box,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  Grid,
  IconButton,
  Paper,
  Stack,
  TextField,
  Tooltip,
  Typography,
  useMediaQuery,
  useTheme
} from "@mui/material";
import RefreshIcon from "@mui/icons-material/Refresh";
import CloseIcon from "@mui/icons-material/Close";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import WarningAmberIcon from "@mui/icons-material/WarningAmber";
import CopyToClipboardWrapper from "../../../../components/copyToClipboardWrapper/CopyToClipboardWrapper";
import CustomTableWithPagination from "../../../../components/customTable/CustomTable";
import BadgePage from "../../../../components/badge/BadgePage";
import {
  defaultRecoveryStatusColor,
  recoveryStatusColor
} from "../../../../utils/commonConstants";
import CustomButton from "../../../../components/CustomButton/Button";
import { getFormattedDate } from "../../../../utils/commonFunctions";
import OtpProofCard, { initialLegState, LegState } from "./OtpProofCard";
import {
  EmailChangeAction,
  COMPLETE_STEP_LABELS,
  COMPLETE_STEP_ORDER,
  CompleteEmailChangeResponse,
  EmailChangeError,
  PreCheck,
  RecoverySessionRecord,
  StartRecoveryResponse,
  completeEmailChange,
  getEmailChangeSessions,
  isActiveRecovery,
  isCompleteSuccess,
  isPreCheckPass,
  isStepDone,
  isStepFailed,
  parseEmailChangeError,
  postEmailChangeOtp,
  startEmailChange
} from "../emailChangeApi";

/**
 * Approximate code lifetimes. The backend values are environment-configurable,
 * so these only drive the on-screen "expires in ~" hint — the authoritative
 * answer is always OTP_EXPIRED coming back from a verify.
 */
const SMS_LIFETIME_SECONDS = 15 * 60;
const EMAIL_LIFETIME_SECONDS = 5 * 60;

const EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/;

interface SessionView {
  recoverySessionId: string;
  /** full address — only known when this screen opened the recovery */
  newEmail: string | null;
  newEmailMasked: string | null;
  mobileOnAccount: string | null;
  mobileVerified: boolean;
  emailVerified: boolean;
  createdAt: string | null;
  lastActivityAt: string | null;
  expiresAt: string | null;
  requestedBy: string | null;
  superseded: boolean;
  preChecks: PreCheck[] | null;
}

/**
 * CustomButton is a styled-components Button carrying `margin-top: 11px`.
 * MUI's `sx` is emotion, so a plain `mt: 0` has equal specificity in a
 * different stylesheet and loses the cascade — the button then hangs ~11px
 * below whatever input it sits beside. Force it off.
 */
const ROW_BUTTON = { marginTop: "0 !important" };

const statusBadgeColor = (status: string) =>
  recoveryStatusColor[(status || "").trim().toLowerCase()] ||
  defaultRecoveryStatusColor;

/** Ticks once a second, but only while something on screen counts down. */
const useTicker = (active: boolean) => {
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    if (!active) return;
    setNow(Date.now());
    const id = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(id);
  }, [active]);
  return now;
};

interface EmailChangeSectionProps {
  userId: string;
  currentEmail: string;
  /** re-read the customer record once the address has actually changed */
  onEmailChanged?: () => void;
}

const EmailChangeSection: React.FC<EmailChangeSectionProps> = ({
  userId,
  currentEmail,
  onEmailChanged
}) => {
  const [loadingState, setLoadingState] = useState(true);
  const [stateError, setStateError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [session, setSession] = useState<SessionView | null>(null);
  /** non-Active rows — superseded/expired attempts, shown for context only */
  const [history, setHistory] = useState<RecoverySessionRecord[]>([]);

  const [newEmail, setNewEmail] = useState("");
  const [confirmEmail, setConfirmEmail] = useState("");
  const [starting, setStarting] = useState(false);
  const [startError, setStartError] = useState<EmailChangeError | null>(null);
  const [startHint, setStartHint] = useState<string | null>(null);
  const [correcting, setCorrecting] = useState(false);

  const [mobileLeg, setMobileLeg] = useState<LegState>(initialLegState);
  const [emailLeg, setEmailLeg] = useState<LegState>(initialLegState);

  const [confirmOpen, setConfirmOpen] = useState(false);
  const [completing, setCompleting] = useState(false);
  const [completeError, setCompleteError] = useState<string | null>(null);
  const [completeHint, setCompleteHint] = useState<string | null>(null);
  const [completeResult, setCompleteResult] =
    useState<CompleteEmailChangeResponse | null>(null);
  const [showHistory, setShowHistory] = useState(true);

  const theme = useTheme();
  const fullScreenDialog = useMediaQuery(theme.breakpoints.down("sm"));

  const now = useTicker(!!session);
  const mountedRef = useRef(true);
  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
    };
  }, []);

  /** which recovery is on screen, readable from async callbacks */
  const sessionIdRef = useRef<string | null>(null);
  useEffect(() => {
    sessionIdRef.current = session?.recoverySessionId ?? null;
  }, [session]);

  const bothVerified = !!session?.mobileVerified && !!session?.emailVerified;
  const nothingProven =
    !!session && !session.mobileVerified && !session.emailVerified;

  const resetLegs = () => {
    setMobileLeg(initialLegState);
    setEmailLeg(initialLegState);
  };

  /**
   * Fold an Active row over what we already know. The GET masks the new
   * address, so a full one captured at /start is kept as long as we are still
   * looking at the same recovery; the same goes for the pre-check report,
   * which only /start returns.
   */
  const mergeActiveRecord = useCallback(
    (
      record: RecoverySessionRecord,
      previous: SessionView | null
    ): SessionView => {
      const sameSession =
        previous?.recoverySessionId === record.RecoverySessionId;
      return {
        recoverySessionId: record.RecoverySessionId,
        newEmail: sameSession ? previous?.newEmail ?? null : null,
        newEmailMasked: record.NewEmailMasked ?? null,
        // MaskedMobile is the backend's transitional duplicate of
        // MobileOnAccount and is being removed — prefer the new field.
        mobileOnAccount:
          record.MobileOnAccount ??
          record.MaskedMobile ??
          (sameSession ? previous?.mobileOnAccount ?? null : null),
        mobileVerified: !!record.MobileVerified,
        emailVerified: !!record.EmailVerified,
        createdAt: record.CreatedAt ?? null,
        lastActivityAt: record.LastActivityAt ?? null,
        expiresAt: record.ExpiresAt ?? null,
        requestedBy: record.RequestedBy ?? null,
        // the GET says nothing about supersession — keep what /start told us
        superseded: sameSession ? previous?.superseded ?? false : false,
        preChecks: sameSession ? previous?.preChecks ?? null : null
      };
    },
    []
  );

  const refreshState = useCallback(
    async (options?: { silent?: boolean }) => {
      if (!userId) return;
      if (!options?.silent) setLoadingState(true);
      setStateError(null);
      try {
        const records = await getEmailChangeSessions(userId);
        if (!mountedRef.current) return;

        const active = records.find(isActiveRecovery) ?? null;
        setHistory(records.filter((record) => record !== active));

        if (!active) {
          setSession(null);
          resetLegs();
          return;
        }
        // A different recovery than the one on screen: start its legs clean.
        if (sessionIdRef.current !== active.RecoverySessionId) resetLegs();
        setSession((previous) => mergeActiveRecord(active, previous));
      } catch (err) {
        if (!mountedRef.current) return;
        setStateError(parseEmailChangeError(err).message);
      } finally {
        if (mountedRef.current && !options?.silent) setLoadingState(false);
      }
    },
    [userId, mergeActiveRecord]
  );

  useEffect(() => {
    setSession(null);
    setNewEmail("");
    setConfirmEmail("");
    setStartError(null);
    setStartHint(null);
    setNotice(null);
    setCorrecting(false);
    setConfirmOpen(false);
    setCompleteError(null);
    setCompleteHint(null);
    setCompleteResult(null);
    setHistory([]);
    setShowHistory(true);
    resetLegs();
    refreshState();
  }, [userId, refreshState]);

  // ---------------------------------------------------------------- start

  const startValidationError = (() => {
    const trimmed = newEmail.trim();
    if (!trimmed) return null;
    if (!EMAIL_PATTERN.test(trimmed)) return "Enter a valid email address.";
    if (trimmed.toLowerCase() === (currentEmail || "").trim().toLowerCase())
      return "That is already the sign-in address on this account.";
    if (
      confirmEmail &&
      trimmed.toLowerCase() !== confirmEmail.trim().toLowerCase()
    )
      return "The two addresses do not match.";
    return null;
  })();

  const canStart =
    !!newEmail.trim() &&
    !!confirmEmail.trim() &&
    !startValidationError &&
    !starting;

  const hintForStartError = (e: EmailChangeError): string | null => {
    if (e.errorCode === "UNAUTHORIZED")
      return "This action is attributed to your support login. Sign out and back in, then retry.";
    if (e.errorCode === "ACCOUNT_NOT_FOUND")
      return "Manual repair — this account cannot go through recovery.";
    if (e.property === "NewEmail" && e.failureType === "DuplicatedValue")
      return "The address already belongs to another account — route this to account-merge.";
    if (e.property === "Account")
      return "Resolve the account record manually before opening a recovery.";
    if (e.property === "Mobile")
      return "No usable mobile on file — follow the manual process (SUP3).";
    if (e.property === "FlowSession")
      return "A recovery with verified proof is already active — refresh to resume it.";
    return null;
  };

  const handleStart = async () => {
    const trimmed = newEmail.trim();
    if (!canStart) return;
    setStarting(true);
    setStartError(null);
    setStartHint(null);
    setNotice(null);
    try {
      const data: StartRecoveryResponse = await startEmailChange(
        userId,
        trimmed
      );
      if (!mountedRef.current) return;
      resetLegs();
      setSession({
        recoverySessionId: data.RecoverySessionId,
        newEmail: data.NewEmail ?? trimmed,
        newEmailMasked: data.NewEmailMasked ?? null,
        mobileOnAccount: data.MobileOnAccount ?? data.MaskedMobile ?? null,
        mobileVerified: !!data.MobileVerified,
        emailVerified: !!data.EmailVerified,
        createdAt: null,
        lastActivityAt: null,
        expiresAt: null,
        requestedBy: null,
        superseded: !!data.Superseded,
        preChecks: data.PreChecks ?? null
      });
      setNewEmail("");
      setConfirmEmail("");
      setCorrecting(false);
      if (data.Existing) {
        setNotice(
          "A recovery for this address was already open — resuming it rather than opening a second one."
        );
      }
      // Fill in the timestamps and RequestedBy that /start does not return.
      refreshState({ silent: true });
    } catch (err) {
      if (!mountedRef.current) return;
      const parsed = parseEmailChangeError(err);
      setStartError(parsed);
      setStartHint(hintForStartError(parsed));
      if (parsed.property === "FlowSession") refreshState({ silent: true });
    } finally {
      if (mountedRef.current) setStarting(false);
    }
  };

  // ------------------------------------------------------------------ otp

  type Leg = "mobile" | "email";

  const setLeg = (leg: Leg, patch: Partial<LegState>) => {
    const apply = (previous: LegState) => ({ ...previous, ...patch });
    if (leg === "mobile") setMobileLeg(apply);
    else setEmailLeg(apply);
  };

  const applyOtpFlags = (mobileVerified: boolean, emailVerified: boolean) =>
    setSession((previous) =>
      previous ? { ...previous, mobileVerified, emailVerified } : previous
    );

  const handleLegError = (leg: Leg, err: any) => {
    const parsed = parseEmailChangeError(err);
    const patch: Partial<LegState> = {
      error: parsed.message,
      hint: null
    };

    switch (parsed.errorCode) {
      case "OTP_INVALID":
        patch.hint = "Ask the customer to read the code back once more.";
        break;
      case "OTP_EXPIRED":
        patch.mustResend = true;
        patch.otp = "";
        patch.hint = "That code has lapsed — send a fresh one.";
        break;
      case "OTP_MAX_ATTEMPTS":
        patch.mustResend = true;
        patch.otp = "";
        patch.hint =
          "The code was destroyed after 5 wrong tries — retyping cannot succeed, send a new one.";
        break;
      case "UNAUTHORIZED":
        patch.hint =
          "This action is attributed to your support login. Sign out and back in, then retry.";
        break;
      case "RATE_LIMIT_EXCEEDED":
        patch.lockedUntil =
          Date.now() + (parsed.retryAfterSeconds ?? 60) * 1000;
        patch.hint = "Wait for the cool-down — do not keep retrying.";
        break;
      case "FLOW_SESSION_NOT_FOUND":
        setSession(null);
        resetLegs();
        setNotice(
          "That recovery is no longer live — it expired or was replaced. Start a new one."
        );
        refreshState({ silent: true });
        return;
      default:
        break;
    }

    if (parsed.property === "FlowSession") {
      patch.hint = "Refreshing the recovery state — act on the message above.";
      refreshState({ silent: true });
    } else if (parsed.property === "Mobile") {
      patch.hint =
        "The mobile on the record is gone or undeliverable — follow the manual process.";
    }

    setLeg(leg, patch);
  };

  const runOtpAction = async (
    leg: Leg,
    action: EmailChangeAction,
    otp?: string
  ) => {
    if (!session) return;
    const sending = action.startsWith("Send");
    setLeg(leg, {
      sending,
      verifying: !sending,
      error: null,
      hint: null
    });
    try {
      const data = await postEmailChangeOtp(
        session.recoverySessionId,
        action,
        otp
      );
      if (!mountedRef.current) return;
      applyOtpFlags(!!data.MobileVerified, !!data.EmailVerified);
      if (sending) {
        // A resend invalidates the previous code for this leg only.
        setLeg(leg, { sentAt: Date.now(), mustResend: false, otp: "" });
      } else {
        setLeg(leg, { otp: "" });
      }
    } catch (err) {
      if (!mountedRef.current) return;
      handleLegError(leg, err);
    } finally {
      if (mountedRef.current) setLeg(leg, { sending: false, verifying: false });
    }
  };

  // ------------------------------------------------------------- complete

  const hintForCompleteError = (e: EmailChangeError): string | null => {
    if (e.errorCode === "UNAUTHORIZED")
      return "This action is attributed to your support login. Sign out and back in, then retry.";
    if (e.property === "FlowSession")
      return "Refreshed the recovery — check both proofs are still recorded, or start again.";
    if (e.property === "NewEmail")
      return "The address has been claimed since this recovery opened — route to account-merge.";
    if (e.property === "Account")
      return "Resolve the account record manually before completing.";
    if (e.property === "Mobile")
      return "Follow the manual process for this account.";
    return null;
  };

  const handleComplete = async () => {
    if (!session || !bothVerified) return;
    setCompleting(true);
    setCompleteError(null);
    setCompleteHint(null);
    setCompleteResult(null);
    try {
      const data = await completeEmailChange(session.recoverySessionId);
      if (!mountedRef.current) return;
      setConfirmOpen(false);
      setCompleteResult(data);

      if (isCompleteSuccess(data)) {
        setSession(null);
        resetLegs();
        // The account now carries the new address — re-read the customer record.
        if (onEmailChanged) onEmailChanged();
      } else {
        // Status "Incomplete": some steps ran, some did not. The account is in
        // a partial state, so re-read both the record and the recovery rather
        // than assuming anything about either.
        if (onEmailChanged) onEmailChanged();
      }
      refreshState({ silent: true });
    } catch (err: any) {
      if (!mountedRef.current) return;
      const parsed = parseEmailChangeError(err);
      setCompleteError(parsed.message);
      setCompleteHint(hintForCompleteError(parsed));
      // Everything the backend rejects here is a 409, including an expired or
      // already-completed session — so always re-read rather than guessing.
      if (parsed.status === 409) refreshState({ silent: true });
    } finally {
      if (mountedRef.current) setCompleting(false);
    }
  };

  // -------------------------------------------------------------- render

  const displayNewEmail =
    session?.newEmail || session?.newEmailMasked || "the new address";
  const displayMobile = session?.mobileOnAccount || "the mobile on the account";

  const expiresInSeconds = session?.expiresAt
    ? Math.ceil((new Date(session.expiresAt).getTime() - now) / 1000)
    : null;
  const sessionExpired = expiresInSeconds !== null && expiresInSeconds <= 0;

  const summaryRows: Array<{ label: string; value: string }> = session
    ? [
        {
          label: "New email address",
          value: session.newEmail || session.newEmailMasked || "—"
        },
        { label: "Mobile", value: session.mobileOnAccount || "—" },
        { label: "Opened by", value: session.requestedBy || "—" },
        {
          label: "Opened at",
          value: session.createdAt ? getFormattedDate(session.createdAt) : "—"
        },
        {
          label: "Last activity",
          value: session.lastActivityAt
            ? getFormattedDate(session.lastActivityAt)
            : "—"
        },
        {
          label: "Session expires",
          value: session.expiresAt
            ? sessionExpired
              ? "Expired"
              : `${getFormattedDate(session.expiresAt)} (in ${Math.floor(
                  (expiresInSeconds as number) / 60
                )} min)`
            : "—"
        }
      ]
    : [];

  const renderCompleteResult = () => {
    if (!completeResult) return null;
    const succeeded = isCompleteSuccess(completeResult);
    const steps = completeResult.Steps || {};
    const known = COMPLETE_STEP_ORDER as readonly string[];
    // Known steps in the order the backend runs them, then anything new it adds.
    const stepKeys = [
      ...known.filter((key) => key in steps),
      ...Object.keys(steps).filter((key) => !known.includes(key))
    ];

    return (
      <Alert
        severity={succeeded ? "success" : "error"}
        sx={{ mb: 2 }}
        onClose={succeeded ? () => setCompleteResult(null) : undefined}
      >
        <AlertTitle>
          {succeeded
            ? "Email address updated"
            : "Change incomplete — escalate this"}
        </AlertTitle>
        {succeeded ? (
          "The sign-in address has been changed and the recovery is closed."
        ) : (
          <>
            Some steps did not finish, so the account is in a partial state. Do
            not retry blindly — raise this with engineering quoting the audit ID
            below.
          </>
        )}

        {completeResult.RecoveryAuditId && (
          <Box display="flex" alignItems="center" flexWrap="wrap" mt={1}>
            <Typography variant="caption" color="text.secondary">
              Audit ID:
            </Typography>
            <Typography
              variant="caption"
              sx={{ ml: 0.5, fontWeight: 600, wordBreak: "break-all" }}
            >
              {completeResult.RecoveryAuditId}
            </Typography>
            <CopyToClipboardWrapper
              text={completeResult.RecoveryAuditId}
              cell="Audit ID"
            />
          </Box>
        )}

        {stepKeys.length > 0 && (
          <Stack spacing={0.75} mt={1.5}>
            {stepKeys.map((key) => {
              const value = steps[key];
              return (
                <Box
                  key={key}
                  display="flex"
                  alignItems="center"
                  flexWrap="wrap"
                  gap={1}
                >
                  <Typography
                    variant="caption"
                    sx={{ minWidth: "150px", fontWeight: 500 }}
                  >
                    {COMPLETE_STEP_LABELS[key] || key}
                  </Typography>
                  <Chip
                    size="small"
                    variant="outlined"
                    color={
                      isStepDone(value)
                        ? "success"
                        : isStepFailed(value)
                        ? "error"
                        : "default"
                    }
                    label={value}
                    sx={{ maxWidth: "100%", height: "auto", py: 0.25 }}
                  />
                </Box>
              );
            })}
          </Stack>
        )}
      </Alert>
    );
  };

  /**
   * Earlier attempts, in the shape the rest of the portal uses for tables.
   * These rows are read-only by definition — only an Active recovery can be
   * sent to or verified — so there is no row click handler.
   */
  const historyColumns = [
    {
      id: "Status",
      label: "Status",
      minWidth: 130,
      align: "left",
      format: (value: any) => (
        <BadgePage color={statusBadgeColor(value)}>
          {value || "Unknown"}
        </BadgePage>
      )
    },
    {
      id: "OldEmail",
      label: "Old Email",
      minWidth: 190,
      align: "left",
      format: (value: any, row: any) => value ?? row?.OldEmailMasked ?? "-"
    },
    {
      id: "NewEmail",
      label: "New Email",
      minWidth: 190,
      align: "left",
      format: (value: any, row: any) => value ?? row?.NewEmailMasked ?? "-"
    },
    {
      id: "ProcessedBy",
      label: "Processed By",
      minWidth: 200,
      align: "left",
      format: (value: any, row: any) => value ?? row?.RequestedBy ?? "-"
    },
    {
      id: "ProcessedAt",
      label: "Processed At",
      minWidth: 180,
      align: "left",
      format: (value: any, row: any) => {
        const at = value ?? row?.LastActivityAt ?? row?.CreatedAt;
        return at ? getFormattedDate(at) : "-";
      }
    }
  ];

  const renderHistory = () => (
    <Box mb={4}>
      <Box
        display="flex"
        alignItems="center"
        justifyContent="space-between"
        flexWrap="wrap"
        gap={1}
        mb={1.5}
      >
        <Typography
          sx={{
            fontFamily: "Montserrat",
            fontWeight: 600,
            fontSize: "14px",
            color: "#192F67"
          }}
        >
          Earlier Recoveries ({history.length})
        </Typography>
        <CustomButton
          callBack={() => setShowHistory((previous) => !previous)}
          styles={{ ...ROW_BUTTON, minHeight: "32px" }}
        >
          {showHistory ? "Hide" : "Show"}
        </CustomButton>
      </Box>

      {showHistory && (
        <Box sx={{ width: "100%", overflowX: "auto" }}>
          <CustomTableWithPagination
            columns={historyColumns}
            data={history}
            apiStatus="idle"
            isShowPagination={false}
            noDataFoundMessage="No earlier recoveries."
          />
        </Box>
      )}
    </Box>
  );

  const renderStartForm = () => (
    <Box>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        Enter the address the customer wants to sign in with. It is pinned to
        the recovery when you open it and cannot be changed on the later steps —
        a typo simply never verifies, because the code goes to the typo.
      </Typography>
      <Grid container spacing={2} alignItems="flex-start">
        <Grid item xs={12} sm={6} lg={3}>
          <TextField
            size="small"
            fullWidth
            label="Current sign-in email"
            value={currentEmail || "—"}
            InputProps={{ readOnly: true }}
            disabled
          />
        </Grid>
        <Grid item xs={12} sm={6} lg={3}>
          <TextField
            size="small"
            fullWidth
            label="New email address"
            value={newEmail}
            onChange={(e) => {
              setNewEmail(e.target.value);
              setStartError(null);
              setStartHint(null);
            }}
            disabled={starting}
            error={!!startValidationError}
          />
        </Grid>
        <Grid item xs={12} sm={6} lg={3}>
          <TextField
            size="small"
            fullWidth
            label="Confirm new email address"
            value={confirmEmail}
            onChange={(e) => {
              setConfirmEmail(e.target.value);
              setStartError(null);
              setStartHint(null);
            }}
            disabled={starting}
            error={!!startValidationError}
            helperText={
              startValidationError || "Must match — guards against mishearing."
            }
          />
        </Grid>
        <Grid item xs={12} sm={6} lg={3}>
          <Stack
            direction="row"
            spacing={1.5}
            flexWrap="wrap"
            useFlexGap
            sx={{ rowGap: 1.5 }}
          >
            <CustomButton
              callBack={handleStart}
              disabled={!canStart}
              styles={{
                ...ROW_BUTTON,
                minHeight: "40px",
                flex: { xs: "1 1 100%", sm: "1 1 auto" },
                minWidth: "150px"
              }}
            >
              {starting ? (
                <CircularProgress size={18} color="inherit" />
              ) : correcting ? (
                "Replace recovery"
              ) : (
                "Start recovery"
              )}
            </CustomButton>
            {correcting && (
              <CustomButton
                callBack={() => {
                  setCorrecting(false);
                  setNewEmail("");
                  setConfirmEmail("");
                  setStartError(null);
                  setStartHint(null);
                }}
                disabled={starting}
                styles={{
                  ...ROW_BUTTON,
                  minHeight: "40px",
                  flex: { xs: "1 1 100%", sm: "0 0 auto" }
                }}
              >
                Cancel
              </CustomButton>
            )}
          </Stack>
        </Grid>
      </Grid>

      {startError && (
        <Alert severity="error" sx={{ mt: 2 }}>
          {startError.message}
          {startHint && (
            <Typography variant="caption" display="block" sx={{ mt: 0.5 }}>
              {startHint}
            </Typography>
          )}
        </Alert>
      )}
    </Box>
  );

  return (
    <Paper
      variant="outlined"
      sx={{
        p: { xs: 2, md: 3 },
        mt: 4,
        borderRadius: "8px",
        width: "100%"
      }}
    >
      <Box
        display="flex"
        alignItems="flex-start"
        justifyContent="space-between"
        gap={2}
      >
        <Box>
          <Typography
            sx={{
              fontFamily: "Montserrat",
              fontWeight: 600,
              fontSize: "18px",
              color: "#192F67"
            }}
          >
            Change Sign-in Email
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
            Account recovery for a customer who has lost access to their sign-in
            email. Keep the customer on the call — the codes expire in minutes.
          </Typography>
        </Box>
        <Tooltip title="Refresh recovery state">
          <span>
            <IconButton
              onClick={() => refreshState()}
              disabled={loadingState}
              size="small"
            >
              <RefreshIcon />
            </IconButton>
          </span>
        </Tooltip>
      </Box>

      <Divider sx={{ my: 2 }} />

      {notice && (
        <Alert severity="info" sx={{ mb: 2 }} onClose={() => setNotice(null)}>
          {notice}
        </Alert>
      )}
      {stateError && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {stateError}
        </Alert>
      )}

      {renderCompleteResult()}

      {!loadingState && history.length > 0 && renderHistory()}

      {loadingState ? (
        <Box display="flex" alignItems="center" gap={1.5} py={2}>
          <CircularProgress size={20} />
          <Typography variant="body2" color="text.secondary">
            Checking for a recovery in progress…
          </Typography>
        </Box>
      ) : !session ? (
        <>
          <Alert severity="info" sx={{ mb: 2 }}>
            No recovery in progress for this account.
          </Alert>
          {renderStartForm()}
        </>
      ) : (
        <>
          <Box
            display="flex"
            alignItems="center"
            flexWrap="wrap"
            gap={1.5}
            mb={1.5}
          >
            <Typography
              sx={{
                fontFamily: "Montserrat",
                fontWeight: 600,
                fontSize: "14px",
                color: "#192F67"
              }}
            >
              Current recovery
            </Typography>
            <BadgePage
              color={statusBadgeColor(sessionExpired ? "Expired" : "Active")}
            >
              {sessionExpired ? "Expired" : "Active"}
            </BadgePage>
          </Box>

          {session.superseded && (
            <Alert severity="info" sx={{ mb: 2 }}>
              This replaced an earlier, unproven recovery. Any codes already
              sent for the previous address no longer work.
            </Alert>
          )}
          {sessionExpired && (
            <Alert severity="warning" sx={{ mb: 2 }}>
              This recovery has passed its expiry. Refresh — you will most
              likely need to start a new one.
            </Alert>
          )}

          <Paper
            variant="outlined"
            sx={{
              p: { xs: 1.5, md: 2 },
              mb: 3,
              borderRadius: "8px",
              background: "#FAFBFD"
            }}
          >
            <Grid container spacing={2}>
              {summaryRows.map((row) => (
                <Grid item xs={12} sm={6} lg={4} key={row.label}>
                  <Typography variant="caption" color="text.secondary">
                    {row.label}
                  </Typography>
                  <Typography
                    sx={{
                      fontFamily: "Montserrat",
                      fontSize: "14px",
                      fontWeight: 500,
                      wordBreak: "break-all"
                    }}
                  >
                    {row.value}
                  </Typography>
                </Grid>
              ))}
            </Grid>

            {session.preChecks && session.preChecks.length > 0 && (
              <>
                <Divider sx={{ my: 1.5 }} />
                <Stack spacing={1}>
                  {/* Backend order, backend wording — Text is rendered as-is.
                      An "Attention" row is a warning for the agent to read
                      out, not a failure: the recovery is open either way. */}
                  {session.preChecks.map((check) => {
                    const passed = isPreCheckPass(check);
                    return (
                      <Box
                        key={check.Code}
                        display="flex"
                        alignItems="flex-start"
                        gap={1}
                      >
                        {passed ? (
                          <CheckCircleIcon
                            fontSize="small"
                            color="success"
                            sx={{ mt: "1px", flexShrink: 0 }}
                          />
                        ) : (
                          <WarningAmberIcon
                            fontSize="small"
                            color="warning"
                            sx={{ mt: "1px", flexShrink: 0 }}
                          />
                        )}
                        <Typography
                          variant="body2"
                          color={passed ? "text.primary" : "warning.main"}
                        >
                          {check.Text}
                        </Typography>
                      </Box>
                    );
                  })}
                </Stack>
              </>
            )}
          </Paper>

          <Grid container spacing={2}>
            <Grid item xs={12} lg={6}>
              <OtpProofCard
                step={1}
                title="Mobile verification"
                instruction="Text a code to the number already on the account, and have the customer read it back."
                target={displayMobile}
                verified={session.mobileVerified}
                state={mobileLeg}
                lifetimeSeconds={SMS_LIFETIME_SECONDS}
                now={now}
                disabled={sessionExpired}
                onOtpChange={(otp) => setLeg("mobile", { otp, error: null })}
                onSend={() => runOtpAction("mobile", "SendMobileOtp")}
                onVerify={() =>
                  runOtpAction("mobile", "VerifyMobileOtp", mobileLeg.otp)
                }
              />
            </Grid>
            <Grid item xs={12} lg={6}>
              <OtpProofCard
                step={2}
                title="New mailbox verification"
                instruction="Email a code to the new address, and have the customer read it back."
                target={displayNewEmail}
                verified={session.emailVerified}
                state={emailLeg}
                lifetimeSeconds={EMAIL_LIFETIME_SECONDS}
                now={now}
                disabled={sessionExpired}
                onOtpChange={(otp) => setLeg("email", { otp, error: null })}
                onSend={() => runOtpAction("email", "SendEmailOtp")}
                onVerify={() =>
                  runOtpAction("email", "VerifyEmailOtp", emailLeg.otp)
                }
              />
            </Grid>
          </Grid>

          {bothVerified && (
            <Box mt={3}>
              <Alert severity="success">
                <AlertTitle>Verifications complete</AlertTitle>
                Both verifications are recorded. Nothing has changed on the
                account yet — apply the change to move the sign-in address
                across.
              </Alert>
              <Paper
                variant="outlined"
                sx={{ p: { xs: 2, md: 2.5 }, mt: 2, borderRadius: "8px" }}
              >
                <Stack
                  direction={{ xs: "column", sm: "row" }}
                  spacing={3}
                  sx={{ mb: 2 }}
                >
                  <Box>
                    <Typography variant="caption" color="text.secondary">
                      Current sign-in email
                    </Typography>
                    <Typography
                      sx={{
                        fontFamily: "Montserrat",
                        fontSize: "14px",
                        fontWeight: 500,
                        wordBreak: "break-all"
                      }}
                    >
                      {currentEmail || "—"}
                    </Typography>
                  </Box>
                  <Box>
                    <Typography variant="caption" color="text.secondary">
                      New sign-in email
                    </Typography>
                    <Typography
                      sx={{
                        fontFamily: "Montserrat",
                        fontSize: "14px",
                        fontWeight: 600,
                        color: "#192F67",
                        wordBreak: "break-all"
                      }}
                    >
                      {session.newEmail || session.newEmailMasked || "—"}
                    </Typography>
                  </Box>
                </Stack>

                <CustomButton
                  callBack={() => {
                    setCompleteError(null);
                    setCompleteHint(null);
                    setCompleteResult(null);
                    setConfirmOpen(true);
                  }}
                  disabled={completing || sessionExpired}
                  styles={{
                    ...ROW_BUTTON,
                    minHeight: "40px",
                    minWidth: "200px",
                    width: { xs: "100%", sm: "auto" }
                  }}
                >
                  {completing ? (
                    <CircularProgress size={18} color="inherit" />
                  ) : (
                    "Update Email"
                  )}
                </CustomButton>

                {completeError && (
                  <Alert severity="error" sx={{ mt: 2 }}>
                    {completeError}
                    {completeHint && (
                      <Typography
                        variant="caption"
                        display="block"
                        sx={{ mt: 0.5 }}
                      >
                        {completeHint}
                      </Typography>
                    )}
                  </Alert>
                )}
              </Paper>
            </Box>
          )}

          {nothingProven && !correcting && (
            <Box mt={3}>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                Typed the wrong address? Nothing is proven yet, so you can
                replace this recovery with a corrected one.
              </Typography>
              <CustomButton
                callBack={() => {
                  setCorrecting(true);
                  setStartError(null);
                  setStartHint(null);
                }}
                styles={{ ...ROW_BUTTON, minHeight: "36px" }}
              >
                Correct the new email address
              </CustomButton>
            </Box>
          )}

          {correcting && (
            <Box mt={3}>
              <Divider sx={{ mb: 2 }} />
              {renderStartForm()}
            </Box>
          )}

          <Typography
            variant="caption"
            color="text.secondary"
            display="block"
            sx={{ mt: 3 }}
          >
            There is no cancel step — an idle recovery expires on its own, so
            closing this screen is enough. Send and verify work on the same
            recovery whichever agent drives it.
          </Typography>
        </>
      )}

      <Dialog
        open={confirmOpen}
        onClose={() => !completing && setConfirmOpen(false)}
        maxWidth="sm"
        fullWidth
        fullScreen={fullScreenDialog}
      >
        <DialogTitle>
          <Box
            display="flex"
            alignItems="center"
            justifyContent="space-between"
          >
            <Typography
              sx={{
                fontWeight: 600,
                fontSize: "18px",
                lineHeight: 1.25,
                fontFamily: "Montserrat",
                fontStyle: "normal",
                color: "#192F67"
              }}
            >
              Confirm Email Address Change
            </Typography>
            <IconButton
              aria-label="close"
              onClick={() => !completing && setConfirmOpen(false)}
              disabled={completing}
              sx={{
                color: (theme) =>
                  completing ? theme.palette.grey[400] : theme.palette.grey[500]
              }}
            >
              <CloseIcon />
            </IconButton>
          </Box>
        </DialogTitle>
        <DialogContent dividers>
          <Typography sx={{ mb: 2 }}>
            This moves the customer's sign-in address across. They will sign in
            with the new address from now on, and the old one stops working.
          </Typography>
          <Stack spacing={1.5}>
            <Box>
              <Typography variant="caption" color="text.secondary">
                From
              </Typography>
              <Typography sx={{ wordBreak: "break-all" }}>
                {currentEmail || "—"}
              </Typography>
            </Box>
            <Box>
              <Typography variant="caption" color="text.secondary">
                To
              </Typography>
              <Typography sx={{ fontWeight: 600, wordBreak: "break-all" }}>
                {session?.newEmail || session?.newEmailMasked || "—"}
              </Typography>
            </Box>
          </Stack>
          {completeError && (
            <Alert severity="error" sx={{ mt: 2 }}>
              {completeError}
            </Alert>
          )}
        </DialogContent>
        <DialogActions>
          <CustomButton
            callBack={() => !completing && setConfirmOpen(false)}
            disabled={completing}
          >
            Cancel
          </CustomButton>
          <CustomButton
            callBack={handleComplete}
            disabled={completing}
            styles={{ minHeight: "36px" }}
          >
            {completing ? (
              <CircularProgress size={20} color="inherit" />
            ) : (
              "Update Email"
            )}
          </CustomButton>
        </DialogActions>
      </Dialog>
    </Paper>
  );
};

export default EmailChangeSection;
